<template>
  <div>
    <div v-if="!loadingIncident">
      <page-header>
        <template #title>
          <h1 class="page-title">
            FNOL
            <small>(First Notification of Loss)</small>
            - {{ claimReference }}
            <VulnerabilityLabel v-if="hasVulnerabilities"></VulnerabilityLabel>
          </h1>
        </template>
        <template #actions>
          <incident-download-buttons
            :incident="incident"
            class="btn-group btn-group-sm"
          ></incident-download-buttons>
          <page-header-action @click.prevent="toggleNotes">
            Notes
          </page-header-action>
          <page-header-action
            tag="form-button"
            :indicator="indicator"
            @clicked="moveToQualification"
            v-if="hasPermission('claims.update') && !readonly"
          >
            Move To Qualification
          </page-header-action>
        </template>
      </page-header>
      <div class="row">
        <div class="col-lg-12">
          <claim-handler></claim-handler>

          <div class="card mb-3">
            <incident-tabs
              :source="source"
              v-on:selected="setActive"
              :active="currentView"
              :incident="incident"
              :module="module"
            ></incident-tabs>

            <transition name="component-fade" mode="out-in">
              <incident-source
                :incident="incident"
                :show-cancel="showCancel"
                :readonly="readonly"
                v-on:created="activateTabs"
                v-on:exists="activateTabs"
                v-if="currentView == 'source'"
                v-on:unsaved-data="saveStatus"
              ></incident-source>

              <client
                :incident="incident"
                :module="module"
                :readonly="readonly"
                v-if="currentView == 'client'"
                v-on:unsaved-data="saveStatus"
              ></client>

              <accident-details
                :incident="incident"
                :readonly="readonly"
                v-if="currentView == 'accident_description'"
                v-on:unsaved-data="saveStatus"
              ></accident-details>

              <third-parties
                :incident="incident"
                :module="module"
                :readonly="readonly"
                v-if="currentView == 'third_parties'"
                v-on:unsaved-data="saveStatus"
              ></third-parties>

              <witnesses
                :incident="incident"
                :readonly="readonly"
                v-if="currentView == 'witnesses'"
              ></witnesses>

              <estimate
                :incident="incident"
                :readonly="readonly"
                v-if="currentView == 'estimate'"
                v-on:unsaved-data="saveStatus"
              ></estimate>

              <statements
                :incident="incident"
                :readonly="readonly"
                v-if="currentView == 'statements'"
                v-on:unsaved-data="saveStatus"
              ></statements>

              <documents
                :incident="incident"
                :readonly="readonly"
                v-if="currentView == 'documents'"
              ></documents>

              <email :incident="incident" v-if="currentView == 'email'"></email>
            </transition>
            <div
              class="card-footer text-right"
              v-if="currentView == 'details' && !readonly"
            >
              <button
                class="btn btn-primary btn-raised"
                name="save"
                v-on:click.prevent="submit"
              >
                <i class="icon icon-save"></i> Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <preloader></preloader>
    </div>

    <notes-window
      :show="notes"
      :incident="incident"
      v-on:toggle-note="toggleNotes"
    ></notes-window>
  </div>
</template>

<script>
import Source from './Sections/Source.vue';
import AccidentDetails from './Sections/AccidentDetails.vue';
import Client from './Sections/Client.vue';
import ThirdParties from './Sections/ThirdParties.vue';
import Witnesses from './Sections/Witnesses.vue';
import Estimate from './Sections/Estimate.vue';
import Notes from './../Shared/Notes.vue';
import Documents from './../Shared/Documents.vue';
import Email from './../Shared/Email.vue';
import IncidentTabs from './../Shared/IncidentTabs.vue';
import IncidentDownloadButtons from './../Shared/IncidentDownloadButtons.vue';
import VulnerabilityLabel from '@/components/Modules/Shared/VulnerabilityLabel.vue';
import Statements from './Sections/Statements.vue';
import PageHeader from './../Shared/PageHeader.vue';
import PageHeaderAction from './../Shared/PageHeaderAction.vue';

export default {
  props: {
    incident: {
      type: String,
      required: true
    }
  },

  components: {
    VulnerabilityLabel,
    Statements,
    PageHeader,
    PageHeaderAction,
    'incident-source': Source,
    AccidentDetails,
    Client,
    ThirdParties,
    Witnesses,
    Estimate,
    Documents,
    Email,
    'notes-window': Notes,
    IncidentTabs,
    IncidentDownloadButtons
  },

  data() {
    return {
      notes: false,
      indicator: false,
      downloading: false,
      currentView: 'source',
      unsaved_data: false,
      module: 'FNOL',
      tabs: [
        {
          name: 'source',
          label: 'Source',
          disabled: false
        },
        {
          name: 'client',
          label: 'Client',
          disabled: true
        },
        {
          name: 'accident_description',
          label: 'Accident Description',
          disabled: true
        },
        {
          name: 'third_parties',
          label: 'Third Parties',
          disabled: true
        },
        {
          name: 'witnesses',
          label: 'Witnesses',
          disabled: true
        },
        {
          name: 'estimate',
          label: 'Estimate',
          disabled: true
        },
        {
          name: 'documents',
          label: 'Documents',
          disabled: true
        },
        {
          name: 'statements',
          label: 'Statements',
          disabled: true
        },
        {
          name: 'email',
          label: 'Email',
          disabled: true
        }
      ],
      showCancel: true
    };
  },

  computed: {
    source() {
      return this.$store.getters['Incident/source'];
    },

    readonly() {
      return this.isReadonly();
    },

    claimReference() {
      const source = this.$store.getters['Incident/source'];
      if (source && source.claim_reference) {
        return source.claim_reference;
      }
      return null;
    },

    loadingIncident() {
      return this.$store.getters['Incident/loading'];
    },

    hasVulnerabilities() {
      const vulnerabilities =
        this.$store.getters['Incident/vulnerabilities'] || [];

      return vulnerabilities.length > 0;
    }
  },

  mounted() {
    this.$store.dispatch('Incident/retrieve', this.incident);
  },

  methods: {
    moveToQualification() {
      if (!this.hasPermission('claims.update') || this.readonly) {
        return;
      }

      this.indicator = true;

      window.Ajax.post(`/incidents/${this.incident}/qualification`)
        .then(response => {
          this.route(`/qualification/${this.incident}`);
        })
        .catch(error => {
          window.toastify.error(
            'Error',
            'Sorry, there was an issue moving the incident to qualification.'
          );
        });
    },

    setActive(tab) {
      // check if unsaved data on the current form
      if (this.unsaved_data && this.currentView !== tab) {
        this.saveWarning(tab); // show alert if there is
      } else {
        this.currentView = tab;
      }
    },

    activateTabs() {
      this.tabs.forEach(tab => {
        tab.disabled = false;
      });
    },

    saveStatus(event) {
      this.unsaved_data = event;
    },

    toggleNotes() {
      this.notes = !this.notes;
    },

    saveWarning(tab) {
      window
        .swal({
          title: 'Are you sure?',
          text:
            'You will lose all details entered in this section if you continue!',
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Continue without saving',
          cancelButtonText: 'Cancel and stay in this section'
        })
        .then(
          () => {
            // Go to selected tab discarding changes
            this.currentView = tab;
            this.unsaved_data = false;
          },
          dismiss => {}
        );
    }
  },

  beforeDestroy() {
    this.$store.commit('Incident/clearRecord');
  }
};
</script>
